'use client';
import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import FloorPlanIcon from '../Icons/FloorPlan';
import { VillageFloorImage, FloorImageContent } from '@/services/types';
import 'viewerjs/dist/viewer.css';
import './styles.css';

type Props = {
  villageId: number;
  houseId: number;
  lang: string;
  seeFloorPlanLabel: string;
};

export default function FloorPlanViewerCanvas({
  villageId,
  houseId,
  lang,
  seeFloorPlanLabel,
}: Props) {
  const [viewerInstance, setViewerInstance] = useState<Viewer | null>(null);
  const [images, setImages] = useState<FloorImageContent[]>([]);
  const viewerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function getFloorImages() {
      const response = await fetch(
        `${process.env.API_URL}/villas/${villageId}/villa-houses/${houseId}`,
      );
      const { floors }: VillageFloorImage = await response.json();
      const filteredContent: FloorImageContent[] = [];

      floors.forEach((floor) => {
        floor.translation.map((translation) => {
          if (translation.locale === lang) {
            filteredContent.push(translation);
          }
        });
      });
      setImages(filteredContent);
    }
    getFloorImages();
  }, []);

  useEffect(() => {
    async function handleViewerInstance() {
      const Viewer = (await import('viewerjs')).default;

      const imagesContainer = document.getElementById('images');

      if (viewerRef.current && imagesContainer !== null) {
        const viewer = new Viewer(imagesContainer, {
          inline: false,
          button: true,
          navbar: false,
          title: function (image: HTMLImageElement) {
            return image.alt;
          },
          toolbar: {
            zoomIn: 1,
            zoomOut: 1,
            oneToOne: 0,
            reset: 1,
            prev: 1,
            play: 0,
            next: 1,
            rotateLeft: 0,
            rotateRight: 0,
            flipHorizontal: 0,
            flipVertical: 0,
          },
          tooltip: false,
          movable: true,
          zoomable: true,
          rotatable: false,
          scalable: false,
          transition: true,
          fullscreen: true,
          keyboard: true,
          zIndex: 9999,
          minZoomRatio: window.innerWidth > 900 ? 0.2 : 0.05,
          maxZoomRatio: 1,
        });
        setViewerInstance(viewer);
      }
    }

    handleViewerInstance();

    return () => {
      if (viewerInstance) {
        viewerInstance.destroy();
      }
    };
  }, [viewerRef]);

  const handleShowViewer = () => {
    if (viewerInstance) {
      viewerInstance.show();
    }
  };

  useEffect(() => {
    if (viewerInstance) {
      viewerInstance.update();
    }
  }, [viewerInstance, images]);

  return (
    <>
      <li
        onClick={handleShowViewer}
        className="text-primary flex items-center cursor-pointer">
        <FloorPlanIcon className="fill-current w-6 h-6 mr-2" />
        {seeFloorPlanLabel}
      </li>
      <div className="hidden" id="images" ref={viewerRef}>
        <ul>
          <li>
            {images.map(({ image, title }) => {
              if (image) {
                return (
                  <Image
                    key={image.filename}
                    src={`${process.env.NEXT_PUBLIC_S3_URI}/uploads/${image.path}${image.filename}`}
                    alt={title}
                    fill
                    sizes="(min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 100vw, 100vw"
                    style={{
                      objectFit: 'cover',
                    }}
                  />
                );
              }
            })}
          </li>
        </ul>
      </div>
    </>
  );
}
